import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { vocabApi } from '@/api/vocab';
import { classifyWordAddError } from '@/lib/addWordsQueue';
import { placeBar, type BarPlace } from '@/lib/selectionBarPlace';
import { clearHighlights, highlightRange, isAnswerControl, selectionClickOnLabel } from '@/lib/textHighlight';
import { cn } from '@/lib/utils';

/**
 * SelectionToolbar — the floating text-annotation bar that pops up when the
 * student selects text inside `containerRef` (e.g. an IELTS reading passage),
 * mirroring the computer-based-test experience. Actions:
 *   • Highlight — wrap the selection in a brand-tinted <mark>.
 *   • Copy      — copy the selection to the clipboard.
 *   • Add to dictionary — push the selected word/phrase into the student's
 *     vocabulary list (vocabApi.addToMyList) so it feeds the Словарь + SRS.
 *
 * It renders nothing until there is a non-empty selection within the container.
 *
 * На компьютере (21.10.2026, T4) панель стоит и на вопросах мока — инструкции, тексты вопросов, варианты: маркер
 * без правки DOM (`lib/textHighlight.ts` — почему), клик по полю/кружку/флажку панель не вызывает, протяжка
 * выделения по варианту его не отмечает.
 *
 * 🔴 ТЕЛЕФОН — ТО ЖЕ САМОЕ (27.10.2026, сборка 1.1.2, слово владельца: «всё как на сайте»). Прежде телефон жил
 * по-старому: маркер `<mark>` с правкой DOM и вызов панели только по `mouseup` — а выделение долгим нажатием
 * `mouseup` не шлёт, и панели на телефоне не было вовсе. Теперь: палец (последний `pointerdown` в контейнере —
 * не мышь) → панель по `selectionchange`, когда выделение улеглось; встаёт ПОД выделением, над ним меню системы
 * (`lib/selectionBarPlace.ts`). Выделение запоминается при показе: нажатие на кнопку панели на iOS может снять
 * выделение раньше, чем дойдёт `click`. Тап по варианту по-прежнему выбирает его — выделение начинается только
 * долгим нажатием.
 */
type Pos = BarPlace & { text: string };

/** Пауза после последнего движения ручек выделения, после которой панель показывается. */
const SETTLE_MS = 300;

export function SelectionToolbar({ containerRef }: { containerRef: React.RefObject<HTMLElement> }) {
  const { t } = useTranslation();
  const [pos, setPos] = useState<Pos | null>(null);
  const [saved, setSaved] = useState(false);
  const [translation, setTranslation] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  // Почему слово не легло в словарь — раньше отказ молчал (промпт 12, 18.10.2026).
  const [addError, setAddError] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  // Выделение на момент показа панели — действия берут его, а не живое (см. описание).
  const rangeRef = useRef<Range | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // В приложении выделение текста выключено везде (`html.native` в index.css) — кроме таких мест, где оно
    // и есть инструмент: маркер и «в словарь». Отметка на контейнере возвращает выделение только здесь;
    // в браузере она ничего не меняет.
    el.setAttribute('data-selectable', '');
    // Текст вариантов — <label> — по общему правилу index.css не выделяется (против лупы на органах управления).
    // Панель нужна и на вариантах: отметка возвращает выделение внутри контейнера.
    el.setAttribute('data-select-answers', '');

    let touch = false;
    let settle: ReturnType<typeof setTimeout> | undefined;

    const show = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return setPos(null);
      const range = sel.getRangeAt(0);
      if (!el.contains(range.commonAncestorContainer)) return setPos(null);
      const text = sel.toString().replace(/\s+/g, ' ').trim();
      if (!text) return setPos(null);
      rangeRef.current = range.cloneRange();
      setSaved(false);
      setTranslation(null);
      setTranslating(false);
      setPos({ ...placeBar(range.getBoundingClientRect(), window.innerWidth, touch), text });
      setAddError(null);
    };

    const onPointer = (e: PointerEvent) => {
      touch = e.pointerType !== 'mouse';
    };

    const showFromMouse = (e: MouseEvent) => {
      if (touch) return;
      if (isAnswerControl(e.target)) return setPos(null);
      show();
    };

    // Палец: ручки выделения двигаются — ждём, пока улягутся. Снятое выделение панель не прячет: её прячет
    // нажатие мимо неё (`onDown`), иначе нажатие на саму панель на iOS успевало её убрать.
    const onSelectionChange = () => {
      if (!touch) return;
      clearTimeout(settle);
      settle = setTimeout(() => {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed) show();
      }, SETTLE_MS);
    };

    const onDown = (e: MouseEvent) => {
      // keep the bar open when clicking its own buttons
      if (barRef.current?.contains(e.target as Node)) return;
      setPos(null);
    };

    // Протяжка выделения по тексту варианта заканчивается кликом по <label> — без этого вариант отмечался сам.
    const onClick = (e: MouseEvent) => {
      if (selectionClickOnLabel(e.target, window.getSelection())) e.preventDefault();
    };

    // Панель стоит над экраном (`fixed`), текст под ней прокручивается — едет следом за выделением.
    const onScroll = () => {
      const r = rangeRef.current;
      if (!r) return;
      setPos((p) => (p ? { ...p, ...placeBar(r.getBoundingClientRect(), window.innerWidth, p.below) } : p));
    };

    el.addEventListener('pointerdown', onPointer, true);
    el.addEventListener('mouseup', showFromMouse);
    el.addEventListener('click', onClick, true);
    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      clearTimeout(settle);
      el.removeEventListener('pointerdown', onPointer, true);
      el.removeEventListener('mouseup', showFromMouse);
      el.removeEventListener('click', onClick, true);
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, { capture: true });
      clearHighlights(el);
      el.removeAttribute('data-select-answers');
    };
  }, [containerRef]);

  if (!pos) return null;

  const withSelection = (fn: (range: Range, text: string) => void) => {
    const r = rangeRef.current;
    if (r && !r.collapsed) fn(r, pos.text);
  };

  const highlight = () =>
    withSelection((range) => {
      try {
        highlightRange(range);
      } catch {
        /* запасной <mark> не лёг — без подсветки, но и без поломки текста */
      }
      window.getSelection()?.removeAllRanges();
      rangeRef.current = null;
      setPos(null);
    });

  const copy = () => {
    navigator.clipboard?.writeText(pos.text).catch(() => {});
    setPos(null);
  };

  const addToDict = async () => {
    setAddError(null);
    try {
      await vocabApi.addToMyList(pos.text);
      setSaved(true);
    } catch (err) {
      setSaved(false);
      const f = classifyWordAddError(err);
      setAddError(
        f.kind === 'limit'
          ? t('blockRenderers.wordLimitTomorrow')
          : f.kind === 'tooFast'
            ? t('blockRenderers.wordTooFast', { seconds: f.retryAfterSec ?? 5 })
            : t('blockRenderers.wordAddError'),
      );
    }
  };

  // Translate keeps the bar open (unlike copy/highlight) so the student can read
  // the result inline underneath the actions.
  const translate = async () => {
    if (translating) return;
    setTranslating(true);
    try {
      const tr = await vocabApi.translate(pos.text);
      setTranslation(tr || '—');
    } catch {
      setTranslation('—');
    } finally {
      setTranslating(false);
    }
  };

  const btn = 'flex h-9 w-9 items-center justify-center rounded-button text-on-surface transition hover:bg-surface-container-high';

  return (
    <div
      ref={barRef}
      className={cn('fixed z-[80] -translate-x-1/2', !pos.below && '-translate-y-full')}
      style={{ left: pos.left, top: pos.top }}
      data-selection-bar={pos.below ? 'below' : 'above'}
    >
      <div className="flex items-center gap-0.5 rounded-pill bg-surface-container-lowest p-1 shadow-ambient ring-1 ring-outline-variant">
        <button type="button" className={btn} title={t('ielts.selection.highlight')} onClick={highlight}>
          <span className="material-symbols-outlined text-primary">ink_highlighter</span>
        </button>
        <button
          type="button"
          className={cn(btn, translation !== null && 'text-primary')}
          title={t('ielts.selection.translate')}
          onClick={translate}
        >
          <span className="material-symbols-outlined">translate</span>
        </button>
        <button type="button" className={btn} title={t('ielts.selection.copy')} onClick={copy}>
          <span className="material-symbols-outlined">content_copy</span>
        </button>
        <button
          type="button"
          className={cn(btn, saved && 'text-success')}
          title={t('ielts.selection.addToVocab')}
          onClick={addToDict}
        >
          <span className="material-symbols-outlined">{saved ? 'task_alt' : 'book'}</span>
        </button>
      </div>
      {(translating || translation !== null) && (
        <div className="mx-auto mt-1 max-w-xs rounded-card bg-surface-container-lowest px-3 py-2 text-center text-sm text-on-surface shadow-ambient ring-1 ring-outline-variant">
          {translating ? (
            <span className="text-on-surface-variant">{t('ielts.selection.translating')}</span>
          ) : (
            translation
          )}
        </div>
      )}
      {addError && (
        <div
          role="status"
          className="mx-auto mt-1 max-w-xs rounded-card bg-surface-container-lowest px-3 py-2 text-center text-sm text-error shadow-ambient ring-1 ring-outline-variant"
        >
          {addError}
        </div>
      )}
    </div>
  );
}
